<template>
	<div class="simulation-snapshot-report-wrapper">
		<content v-if="reportData">
			<div class="header">
				<img
					src="@/assets/images/Logo_JA.png"
					alt="Junior Achievement Logo"
					class="ja-logo"
				/>
				<div class="ja-title">
					JA Finance Park <span class="trademark">&reg;</span>
				</div>
			</div>
			<h1>{{ $t('simulationSnapshot') }}</h1>
			<h3>
				{{
					$t('totalStudentsInSimulation', {
						studentsCount: reportData.totalNumberOfStudents
					})
				}}
			</h3>
			<h3>
				{{
					$t('totalOfStudentsCompleted', {
						studentsCount:
							reportData.totalNumberOfStudentsWithSimulationCompleted
					})
				}}
			</h3>
			<div class="wrapper">
				<div
					v-for="lifeStage in reportData.lifeStages"
					:key="lifeStage.lifeStageName"
					class="iterator"
				>
					<ja-legend-donut-chart
						:id="lifeStage.lifeStageName"
						:size="102"
						:value="getPctOfStudentsPerLifeStages(lifeStage.numberOfStudents)"
						:label-chart-a="lifeStage.numberOfStudents"
						:label-chart-b="
							reportData.totalNumberOfStudents - lifeStage.numberOfStudents
						"
						:title="lifeStage.lifeStageName"
						:chart-legend-a="
							$t('ofStudentsCompleted', {
								studentsPercentage: getPctOfStudentsPerLifeStages(
									lifeStage.numberOfStudents
								)
							})
						"
						:chart-legend-b="
							$t('ofStudentsNotCompleted', {
								studentsPercentage: getRemainingPercentage(
									lifeStage.numberOfStudents
								)
							})
						"
						chart-color-a="#00a0af"
						chart-color-b="#285f74"
					></ja-legend-donut-chart>
				</div>
			</div>
		</content>
		<content v-else class="no-results">
			<h1>{{ $t('noResultsFound') }}</h1>
		</content>
		<footer class="sticky-footer">
			<div class="footer-content">
				<ja-button
					class="small"
					name="close-report"
					@click.native.once="closeReport()"
				>
					<span class="button-text">{{ $t('close') }}</span>
				</ja-button>
			</div>
		</footer>
	</div>
</template>

<script>
import gql from 'graphql-tag';
import NavigationLoading from '@/mixins/NavigationLoading';
import AdminFacility from '@/mixins/AdminFacility';
import Button from '@/components/ui/Button';
import LegendDonutChart from '@/components/ui/LegendDonutChart';

export default {
	components: {
		jaButton: Button,
		jaLegendDonutChart: LegendDonutChart
	},
	mixins: [NavigationLoading, AdminFacility],
	data() {
		return {
			reportData: null,
			filters: sessionStorage.getItem('reportFilters')
				? JSON.parse(sessionStorage.getItem('reportFilters'))
				: null
		};
	},
	watch: {
		reportData() {
			this.$store.commit('setLoading', false);
		}
	},
	apollo: {
		reportData: {
			query: gql`
				query simulationSnapshotReportData($where: String!) {
					simulations(where: $where) {
						simulationSnapshotReport {
							totalNumberOfStudents
							totalNumberOfStudentsWithSimulationCompleted
							totalNumberOfStudentsWithSavingsAllocationCompleted
							totalNumberOfStudentsWithAnsweredQuestions
							lifeStages {
								lifeStageName
								lifeStageDisplayOrder
								numberOfStudents
								percentageOfStudents
								numberOfStudentsBillsPaid
								numberOfStudentsFinishedShopping
								numberOfStudentsWithBudgetFinalized
							}
						}
					}
				}
			`,
			variables() {
				const filters = [];

				if (this.selectedFacilityId != null) {
					filters.push(`facility_ID = ${this.selectedFacilityId}`);
				}
				if (this.filters.simulationType) {
					filters.push(`simulationType= "${this.filters.simulationType}"`);
				}
				return {
					where: filters.join('&&')
				};
			},
			fetchPolicy: 'no-cache',
			update(data) {
				const reportData = {
					totalNumberOfStudents: 0,
					totalNumberOfStudentsWithSimulationCompleted: 0,
					lifeStages: [],
					totalLifeStages: []
				};

				if (data.simulations.length) {
					reportData.totalNumberOfStudents = data.simulations.reduce(
						(accumulator, currentValue) => {
							if (currentValue.simulationSnapshotReport) {
								return (
									accumulator +
									currentValue.simulationSnapshotReport.totalNumberOfStudents
								);
							} else {
								return accumulator;
							}
						},
						0
					);
					reportData.totalNumberOfStudentsWithSimulationCompleted = data.simulations.reduce(
						(accumulator, currentValue) => {
							if (currentValue.simulationSnapshotReport) {
								return (
									accumulator +
									currentValue.simulationSnapshotReport
										.totalNumberOfStudentsWithSimulationCompleted
								);
							} else {
								return accumulator;
							}
						},
						0
					);
					data.simulations.forEach(simulation => {
						if (simulation.simulationSnapshotReport) {
							simulation.simulationSnapshotReport.lifeStages.forEach(
								lifeStage => {
									const lifeStages = reportData.totalLifeStages;

									if (lifeStages.length) {
										const currStageData = lifeStages.find(
											stage => stage.lifeStageName === lifeStage.lifeStageName
										);

										if (currStageData) {
											currStageData.numberOfStudents +=
												lifeStage.numberOfStudents;
											currStageData.numberOfStudentsBillsPaid +=
												lifeStage.numberOfStudentsBillsPaid;
											currStageData.numberOfStudentsFinishedShopping +=
												lifeStage.numberOfStudentsFinishedShopping;
											currStageData.numberOfStudentsWithBudgetFinalized +=
												lifeStage.numberOfStudentsWithBudgetFinalized;
										} else {
											lifeStages.push({
												...lifeStage
											});
										}
									} else {
										lifeStages.push({ ...lifeStage });
									}
								}
							);
						}
					});
				}
				const totalNumberOfStudentsWithSavingsAllocationCompleted = data.simulations.reduce(
					(accumulator, currentValue) =>
						accumulator +
						((currentValue.simulationSnapshotReport &&
							currentValue.simulationSnapshotReport
								.totalNumberOfStudentsWithSavingsAllocationCompleted) ||
							0),
					0
				);

				const totalNumberOfStudentsWithAnsweredQuestions = data.simulations.reduce(
					(accumulator, currentValue) =>
						accumulator +
						((currentValue.simulationSnapshotReport &&
							currentValue.simulationSnapshotReport
								.totalNumberOfStudentsWithAnsweredQuestions) ||
							0),
					0
				);

				reportData.lifeStages.push({
					numberOfStudents: totalNumberOfStudentsWithSavingsAllocationCompleted,
					lifeStageName: this.$t('completedSavingsAllocations')
				});
				reportData.lifeStages.push({
					numberOfStudents: totalNumberOfStudentsWithAnsweredQuestions,
					lifeStageName: this.$t('completedProfileSetup')
				});
				reportData.totalLifeStages.forEach(report => {
					if (
						report.hasOwnProperty('numberOfStudentsBillsPaid') &&
						report.lifeStageName.toLowerCase() !== 'teenager'
					) {
						const newObjectBillPaid = {
							key: 'numberOfStudentsBillsPaid',
							lifeStageName: `${report.lifeStageName} ${this.$t(
								'numberOfStudentsBillsPaid'
							)}`,
							numberOfStudents: report.numberOfStudentsBillsPaid
						};
						reportData.lifeStages.push(newObjectBillPaid);
					}
					if (report.hasOwnProperty('numberOfStudentsFinishedShopping')) {
						const newObjectFinishedShopping = {
							key: 'numberOfStudentsFinishedShopping',
							lifeStageName: `${report.lifeStageName} ${this.$t(
								'numberOfStudentsFinishedShopping'
							)}`,
							numberOfStudents: report.numberOfStudentsFinishedShopping
						};
						reportData.lifeStages.push(newObjectFinishedShopping);
					}
					if (report.hasOwnProperty('numberOfStudentsWithBudgetFinalized')) {
						const newObjectBudgetFinalized = {
							key: 'numberOfStudentsWithBudgetFinalized',
							lifeStageName: `${report.lifeStageName} ${this.$t(
								'numberOfStudentsWithBudgetFinalized'
							)}`,
							numberOfStudents: report.numberOfStudentsWithBudgetFinalized
						};
						reportData.lifeStages.push(newObjectBudgetFinalized);
					}
				});

				reportData.lifeStages.sort((stage1, stage2) => {
					const order = {
						teenager: 1,
						'young adult': 2,
						'working adult': 3,
						'adult nearing': 4
					};

					const name1 = stage1.lifeStageName.toLowerCase();
					const name2 = stage2.lifeStageName.toLowerCase();

					const order1 = Object.keys(order).findIndex(stageName =>
						name1.includes(stageName)
					);
					const order2 = Object.keys(order).findIndex(stageName =>
						name2.includes(stageName)
					);

					return order1 - order2;
				});
				return reportData;
			}
		}
	},
	methods: {
		getPctOfStudentsPerLifeStages(studentsCountPerStage) {
			if (!this.reportData.totalNumberOfStudents || !studentsCountPerStage) {
				return 0;
			}
			if (this.reportData.totalNumberOfStudents < studentsCountPerStage) {
				return parseInt(((studentsCountPerStage / 100) * 100).toFixed(0));
			}
			return parseInt(
				(
					(studentsCountPerStage / this.reportData.totalNumberOfStudents) *
					100
				).toFixed(0)
			);
		},
		getRemainingPercentage(studentsCountPerStage) {
			if (!this.reportData.totalNumberOfStudents || !studentsCountPerStage) {
				return 0;
			}
			const calculatedPercentage = this.getPctOfStudentsPerLifeStages(
				studentsCountPerStage
			);
			const remainingPercentage = 100 - calculatedPercentage;
			return remainingPercentage;
		},
		closeReport() {
			this.$router.push({ name: 'reporting' });
		}
	}
};
</script>
<style lang="scss" scoped>
.simulation-snapshot-report-wrapper {
	height: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	background-color: $container-bgcolor;
	-webkit-overflow-scrolling: touch;
	.header {
		margin-top: 32px;
		margin-bottom: 40px;
		display: flex;
		align-items: center;
		flex: 0 0 auto;
		justify-content: center;
		position: relative;
		height: 32px;
	}
	content {
		overflow-x: hidden;
		width: 100%;
		overflow-y: auto;
		flex: 1 1 auto;
		-webkit-flex: 1 1 auto;

		&.no-results {
			display: flex;
			justify-content: center;
			align-items: center;
		}

		.wrapper {
			margin-left: auto;
			margin-right: auto;
			max-width: 1000px;
			display: flex;
			flex-wrap: wrap;
			padding: 24px;
		}

		.iterator {
			min-width: 33.33%;
			margin-bottom: 35px;
			flex: 0 0 calc(33.33% - 16px);
			display: flex;
			justify-content: center;
			align-items: center;
		}

		h1,
		h3 {
			text-align: center;
		}
	}

	footer {
		width: 100%;
		height: 90px;
		padding: 0 5%;
		display: flex;
		position: relative;
		z-index: 1;
		flex: 0 0 auto;
		align-items: center;
		justify-content: flex-end;
		background-color: $container-bgcolor;
		box-shadow: 0 -10px 30px rgba(0, 76, 208, 0.24);

		.footer-content {
			float: right;
			display: inherit;
			align-items: center;

			vertical-align: middle;

			button {
				float: right;
			}
		}
	}
}
</style>
